import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Search,
  Filter,
  Plus,
  MoreVertical,
  User,
  Mail,
  Phone,
  MapPin,
  Building,
  Calendar,
  Eye,
  Edit,
  Trash2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Download,
  Upload,
  CreditCard,
  DollarSign,
} from "lucide-react";
import { toast } from "react-hot-toast";
import api from "../../utils/axios.config";
import { baseUrl } from "../../utils/url";
import division from "../../data/division.json";
import district from "../../data/district.json";
import upazila from "../../data/upazila.json";
import useLogo from "../../hooks/useLogo";

// Helper functions for location data
const getLocationName = (id, data) => {
  if (!id) return "N/A";
  const item = data.find((item) => item.id === id);
  return item?.bn_name || "N/A";
};

const getRoleBadgeColor = (role) => {
  const roleColors = {
    admin: "bg-red-100 text-red-800 border-red-200",
    adviser: "bg-purple-100 text-purple-800 border-purple-200",
    "principal-adviser": "bg-indigo-100 text-indigo-800 border-indigo-200",
    member: "bg-blue-100 text-blue-800 border-blue-200",
    "blood-donor": "bg-rose-100 text-rose-800 border-rose-200",
    volunteer: "bg-green-100 text-green-800 border-green-200",
    "talent-student": "bg-yellow-100 text-yellow-800 border-yellow-200",
    "university-student": "bg-cyan-100 text-cyan-800 border-cyan-200",
    donor: "bg-emerald-100 text-emerald-800 border-emerald-200",
    sponsor: "bg-orange-100 text-orange-800 border-orange-200",
    freelancer: "bg-violet-100 text-violet-800 border-violet-200",
  };
  return roleColors[role] || "bg-gray-100 text-gray-800 border-gray-200";
};

const getStatusBadgeColor = (status) => {
  const statusColors = {
    active: "bg-green-100 text-green-800 border-green-200",
    inactive: "bg-yellow-100 text-yellow-800 border-yellow-200",
    blocked: "bg-red-100 text-red-800 border-red-200",
  };
  return statusColors[status] || "bg-gray-100 text-gray-800 border-gray-200";
};

function AllUser() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [viewMode, setViewMode] = useState("active"); // 'active' or 'trash'
  const [selectedUser, setSelectedUser] = useState(null);
  const [showNnsIdModal, setShowNnsIdModal] = useState(false);
  const [nnsId, setNnsId] = useState("");
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showUserFinanceModal, setShowUserFinanceModal] = useState(false);
  const [selectedUserForFinance, setSelectedUserForFinance] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [userImageBase64, setUserImageBase64] = useState("");
  const [userImageError, setUserImageError] = useState(false);
  const [logoImageError, setLogoImageError] = useState(false);

  const { data: logoUrl } = useLogo();

  const queryClient = useQueryClient();

  // Get current user from localStorage
  const getCurrentUser = () => {
    try {
      const userData = localStorage.getItem("user");
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      return null;
    }
  };

  const currentUser = getCurrentUser();

  // Fetch active users
  const {
    data: usersData,
    isLoading: loadingUsers,
    error: usersError,
    refetch: refetchUsers,
  } = useQuery({
    queryKey: ["users", viewMode],
    queryFn: async () => {
      if (viewMode === "trash") {
        return await api.get("user/trash");
      }
      return await api.get("user");
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const users =
    viewMode === "trash"
      ? usersData?.data?.data?.users || []
      : usersData?.data?.data?.users || [];

  // Mutations
  const changeStatusMutation = useMutation({
    mutationFn: async ({ userId, status }) => {
      return api.patch("user/status", { _id: userId, status });
    },
    onSuccess: () => {
      toast.success("User status updated successfully");
      queryClient.invalidateQueries(["users"]);
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || "Failed to update user status"
      );
    },
  });

  const updateNnsIdMutation = useMutation({
    mutationFn: async ({ userId, nnsID }) => {
      return api.patch("user/nns-id", { userId, nnsID });
    },
    onSuccess: () => {
      toast.success("NNS ID updated successfully");
      queryClient.invalidateQueries(["users"]);
      setShowNnsIdModal(false);
      setNnsId("");
      setSelectedUser(null);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to update NNS ID");
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (userId) => {
      return api.delete(`user/${userId}`);
    },
    onSuccess: () => {
      toast.success("User moved to trash successfully");
      queryClient.invalidateQueries(["users"]);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to delete user");
    },
  });

  const restoreUserMutation = useMutation({
    mutationFn: async (userId) => {
      return api.patch(`user/restore/${userId}`);
    },
    onSuccess: () => {
      toast.success("User restored successfully");
      queryClient.invalidateQueries(["users"]);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to restore user");
    },
  });

  const permanentDeleteMutation = useMutation({
    mutationFn: async (userId) => {
      return api.delete(`user/permanent/${userId}`);
    },
    onSuccess: () => {
      toast.success("User permanently deleted");
      queryClient.invalidateQueries(["users"]);
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || "Failed to permanently delete user"
      );
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async ({ userId, password }) => {
      return api.patch(`user/reset-password/${userId}`, { password });
    },
    onSuccess: () => {
      toast.success("Password reset successfully");
      queryClient.invalidateQueries(["users"]);
      setShowResetPasswordModal(false);
      setNewPassword("");
      setSelectedUser(null);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to reset password");
    },
  });

  // Filter and search users
  const filteredUsers = useMemo(() => {
    let filtered = users;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (user) =>
          user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.mobile?.includes(searchTerm) ||
          user.nnsID?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.membershipId?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((user) => user.status === statusFilter);
    }

    // Role filter
    if (roleFilter !== "all") {
      filtered = filtered.filter((user) => user.role === roleFilter);
    }

    return filtered;
  }, [users, searchTerm, statusFilter, roleFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * usersPerPage,
    currentPage * usersPerPage
  );

  // Handle actions
  const handleStatusChange = (userId, newStatus) => {
    changeStatusMutation.mutate({ userId, status: newStatus });
  };

  const handleNnsIdUpdate = () => {
    if (!nnsId.trim()) {
      toast.error("Please enter a valid NNS ID");
      return;
    }
    updateNnsIdMutation.mutate({
      userId: selectedUser._id,
      nnsID: nnsId.trim(),
    });
  };

  const handleDeleteUser = (userId) => {
    if (window.confirm("Are you sure you want to move this user to trash?")) {
      deleteUserMutation.mutate(userId);
    }
  };

  const handleRestoreUser = (userId) => {
    if (window.confirm("Are you sure you want to restore this user?")) {
      restoreUserMutation.mutate(userId);
    }
  };

  const handlePermanentDelete = (userId) => {
    if (
      window.confirm(
        "Are you sure you want to permanently delete this user? This action cannot be undone."
      )
    ) {
      permanentDeleteMutation.mutate(userId);
    }
  };

  const openResetPasswordModal = (user) => {
    setSelectedUser(user);
    setShowResetPasswordModal(true);
  };

  const handleResetPassword = () => {
    if (!newPassword.trim() || newPassword.trim().length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }
    resetPasswordMutation.mutate({
      userId: selectedUser._id,
      password: newPassword.trim(),
    });
  };

  const openNnsIdModal = (user) => {
    setSelectedUser(user);
    setNnsId(user.nnsID || "");
    setShowNnsIdModal(true);
  };

  const openUserFinanceModal = (user) => {
    setSelectedUserForFinance(user);
    setShowUserFinanceModal(true);
  };

  const convertImageToBase64 = (imageUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        try {
          const dataURL = canvas.toDataURL("image/jpeg", 0.8);
          resolve(dataURL);
        } catch (error) {
          resolve("");
        }
      };
      img.onerror = () => resolve("");
      img.src = imageUrl;
    });
  };

  const handleViewClick = async (user) => {
    setViewingUser(user);
    setShowViewModal(true);
    setUserImageError(false);
    setLogoImageError(false);

    const imageUrl = user.imageUrl?.startsWith("http")
      ? user.imageUrl
      : `${baseUrl}${user.imageUrl}`;
    const base64Image = await convertImageToBase64(imageUrl);
    if (base64Image) {
      setUserImageBase64(base64Image);
    } else {
      setUserImageError(true);
    }
  };

  const handleDownloadIdCard = async (user) => {
    if (!user) {
      toast.error("User data not available.");
      return;
    }

    // Font loading
    await document.fonts.load("48px SolaimanLipi");
    await document.fonts.load("36px SolaimanLipi");
    await document.fonts.load("28px SolaimanLipi");

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = 1011;
    canvas.height = 638;

    // Background Gradient
    const gradient = ctx.createLinearGradient(
      0,
      0,
      canvas.width,
      canvas.height
    );
    gradient.addColorStop(0, "#fffde4"); // Light Yellow
    gradient.addColorStop(1, "#fff0b3"); // Deeper Yellow
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Add a decorative shape
    ctx.fillStyle = "#006400"; // Dark Green from logo
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(canvas.width, 0);
    ctx.lineTo(canvas.width, 230);
    ctx.lineTo(0, 100);
    ctx.closePath();
    ctx.fill();

    const logo = new Image();
    logo.crossOrigin = "anonymous";
    logo.src = logoUrl || "/logo.png";

    const downloadCanvas = () => {
      const link = document.createElement("a");
      link.download = `${user.name}_ID_Card.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("ID Card downloaded.");
    };

    logo.onload = () => {
      // Draw logo
      ctx.drawImage(logo, 50, 30, 120, 120);

      // Header text
      ctx.fillStyle = "white";
      ctx.font = "bold 36px SolaimanLipi";
      ctx.fillText("নিঃস্বার্থ নবজীবন সংগঠন- NNS", 200, 80);
      ctx.font = "28px SolaimanLipi";
      ctx.fillText(
        "স্বার্থপর মানুষের ভিড়ে নিঃস্বার্থভাবে আমরাই অবিচল",
        200,
        120
      );

      // User Details
      ctx.fillStyle = "black";
      ctx.font = "bold 42px SolaimanLipi";
      ctx.fillText(user.name, 450, 280);

      ctx.font = "36px SolaimanLipi";
      ctx.fillText(`NNS ID: ${user.nnsID || "N/A"}`, 450, 340);
      ctx.fillText(`পেশা: ${user.occupation || "N/A"}`, 450, 390);
      ctx.fillText(
        `থানা: ${getLocationName(user.upazila, upazila) || "N/A"}`,
        450,
        440
      );
      ctx.fillText(
        `জেলা: ${getLocationName(user.district, district) || "N/A"}`,
        450,
        490
      );

      // Blood Group in Red
      ctx.fillStyle = "red";
      ctx.fillText(
        `রক্তের গ্রুপ: ${user.bloodGroup?.toUpperCase() || "N/A"}`,
        450,
        540
      );

      // Footer
      ctx.fillStyle = "#006400";
      ctx.font = "bold 32px SolaimanLipi";
      ctx.fillText("শুভেচ্ছান্তে: কার্যকরী পরিষদ", 350, 600);

      // User Image
      const userImg = new Image();
      userImg.crossOrigin = "anonymous";
      userImg.src = user.imageUrl.startsWith("http")
        ? user.imageUrl
        : `${baseUrl}${user.imageUrl}`;

      const drawUserImage = (imageToDraw) => {
        // Draw image with a border
        ctx.drawImage(imageToDraw, 80, 250, 300, 300);
        ctx.strokeStyle = "black";
        ctx.lineWidth = 3;
        ctx.strokeRect(80, 250, 300, 300);
      };

      userImg.onload = () => {
        drawUserImage(userImg);
        downloadCanvas();
      };

      userImg.onerror = () => {
        toast("User image failed to load. Using default avatar.");
        const defaultAvatar = new Image();
        defaultAvatar.crossOrigin = "anonymous";
        defaultAvatar.src = "/default-avatar.png";
        defaultAvatar.onload = () => {
          drawUserImage(defaultAvatar);
          downloadCanvas();
        };
        defaultAvatar.onerror = () => {
          toast.error("Default avatar also failed to load.");
          downloadCanvas();
        };
      };
    };

    logo.onerror = () => {
      toast.error("Logo not found.");
    };
  };

  if (loadingUsers) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {viewMode === "trash" ? "ট্র্যাশ ব্যবহারকারী" : "সকল ব্যবহারকারী"}
            </h1>
            <p className="text-gray-600">
              {viewMode === "trash"
                ? "মুছে ফেলা ব্যবহারকারীদের পরিচালনা করুন"
                : "সিস্টেমের সকল ব্যবহারকারীর তালিকা ও পরিচালনা"}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() =>
                setViewMode(viewMode === "active" ? "trash" : "active")
              }
              className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                viewMode === "trash"
                  ? "bg-green-600 hover:bg-green-700 text-white"
                  : "bg-red-600 hover:bg-red-700 text-white"
              }`}
            >
              {viewMode === "trash" ? (
                <>
                  <RefreshCw size={16} className="mr-2 inline" />
                  সক্রিয় তালিকা
                </>
              ) : (
                <>
                  <Trash2 size={16} className="mr-2 inline" />
                  ট্র্যাশ দেখুন
                </>
              )}
            </button>

            <button
              onClick={() => refetchUsers()}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition-colors duration-200"
            >
              <RefreshCw size={16} className="mr-2 inline" />
              রিফ্রেশ
            </button>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      {viewMode === "active" && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
              <input
                type="text"
                placeholder="নাম, ইমেইল, মোবাইল বা NNS ID দিয়ে খুঁজুন..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">সকল স্ট্যাটাস</option>
              <option value="active">সক্রিয়</option>
              <option value="inactive">নিষ্ক্রিয়</option>
              <option value="blocked">ব্লকড</option>
            </select>

            {/* Role Filter */}
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">সকল ভূমিকা</option>
              <option value="admin">অ্যাডমিন</option>
              <option value="adviser">উপদেষ্টা</option>
              <option value="principal-adviser">প্রধান উপদেষ্টা</option>
              <option value="member">সদস্য</option>
              <option value="blood-donor">রক্তদাতা</option>
              <option value="volunteer">স্বেচ্ছাসেবক</option>
              <option value="talent-student">মেধাবী শিক্ষার্থী</option>
              <option value="university-student">
                বিশ্ববিদ্যালয় শিক্ষার্থী
              </option>
              <option value="donor">দাতা</option>
              <option value="sponsor">স্পন্সর</option>
              <option value="freelancer">ফ্রিল্যান্সার</option>
            </select>

            {/* Results count */}
            <div className="flex items-center text-sm text-gray-600">
              <span className="font-medium">{filteredUsers.length}</span>
              <span className="ml-1">জন ব্যবহারকারী পাওয়া গেছে</span>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-3 font-semibold text-gray-900 text-xs uppercase tracking-wider">
                  ব্যবহারকারী
                </th>
                <th className="text-left py-3 px-3 font-semibold text-gray-900 text-xs uppercase tracking-wider">
                  যোগাযোগ
                </th>
                <th className="text-left py-3 px-3 font-semibold text-gray-900 text-xs uppercase tracking-wider">
                  প্রতিষ্ঠান
                </th>
                <th className="text-left py-3 px-3 font-semibold text-gray-900 text-xs uppercase tracking-wider">
                  NNS ID
                </th>
                <th className="text-left py-3 px-3 font-semibold text-gray-900 text-xs uppercase tracking-wider">
                  স্ট্যাটাস
                </th>
                <th className="text-left py-3 px-3 font-semibold text-gray-900 text-xs uppercase tracking-wider">
                  ভূমিকা
                </th>
                {viewMode === "trash" && (
                  <th className="text-left py-3 px-3 font-semibold text-gray-900 text-xs uppercase tracking-wider">
                    মুছে ফেলা হয়েছে
                  </th>
                )}
                <th className="text-right py-3 px-3 font-semibold text-gray-900 text-xs uppercase tracking-wider">
                  অ্যাকশন
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedUsers.map((user) => (
                <tr
                  key={user._id}
                  className="hover:bg-gray-50 transition-colors duration-150"
                >
                  {/* User Info */}
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-2">
                      <div className="relative flex-shrink-0">
                        <img
                          src={
                            user.imageUrl
                              ? `${baseUrl}${user.imageUrl}`
                              : "/default-avatar.png"
                          }
                          alt={user.name}
                          className="w-8 h-8 rounded-full object-cover border-2 border-gray-200"
                          onError={(e) => {
                            e.target.src = "/default-avatar.png";
                          }}
                        />
                        {user.status === "active" && (
                          <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900 truncate max-w-[120px]" title={user.name}>
                          {user.name}
                        </p>
                        <p className="text-xs text-gray-600 truncate max-w-[120px]" title={`${getLocationName(user.division, division)}, ${getLocationName(user.district, district)}`}>
                          {getLocationName(user.division, division)},{getLocationName(user.district, district)}
                        </p>
                        <p className="text-xs text-gray-500 truncate max-w-[120px]" title={`সদস্য নং: ${user.membershipId}`}>
                          সদস্য নং: {user.membershipId}
                        </p>
                      </div>
                    </div>
                  </td>

                  {/* Contact */}
                  <td className="py-3 px-3">
                    <div className="space-y-1">
                      <div className="flex items-center text-xs text-gray-900 truncate max-w-[120px]" title={user.mobile}>
                        <Phone size={12} className="mr-1 text-gray-400 flex-shrink-0" />
                        {user.mobile}
                      </div>
                      {user.email && (
                        <div className="flex items-center text-xs text-gray-600 truncate max-w-[120px]" title={user.email}>
                          <Mail size={12} className="mr-1 text-gray-400 flex-shrink-0" />
                          {user.email}
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Institution */}
                  <td className="py-3 px-3">
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-gray-900 truncate max-w-[120px]" title={user.institute}>
                        {user.institute}
                      </p>
                      <p className="text-xs text-gray-600 truncate max-w-[120px]" title={user.designation}>
                        {user.designation}
                      </p>
                    </div>
                  </td>

                  {/* NNS ID */}
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-1">
                      <span className="text-xs font-mono text-gray-900 truncate max-w-[100px]" title={user.nnsID || "Not Assigned"}>
                        {user.nnsID || "Not Assigned"}
                      </span>
                      {!user.nnsID && (
                        <AlertTriangle size={12} className="text-yellow-500 flex-shrink-0" />
                      )}
                    </div>
                  </td>

                  {/* Status */}
                  <td className="py-3 px-3">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadgeColor(
                        user.status
                      )}`}
                    >
                      {user.status === "active" && (
                        <CheckCircle size={10} className="mr-1" />
                      )}
                      {user.status === "inactive" && (
                        <Clock size={10} className="mr-1" />
                      )}
                      {user.status === "blocked" && (
                        <XCircle size={10} className="mr-1" />
                      )}
                      {user.status === "active"
                        ? "সক্রিয়"
                        : user.status === "inactive"
                        ? "নিষ্ক্রিয়"
                        : "ব্লকড"}
                    </span>
                  </td>

                  {/* Role */}
                  <td className="py-3 px-3">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(
                        user.role
                      )}`}
                    >
                      {user.role}
                    </span>
                  </td>

                  {/* Deleted Info (for trash view) */}
                  {viewMode === "trash" && (
                    <td className="py-3 px-3">
                      <div className="space-y-1">
                        <p className="text-xs text-gray-600">
                          {new Date(user.deletedAt).toLocaleDateString("bn-BD")}
                        </p>
                        {user.deletedBy && (
                          <p className="text-xs text-gray-500 truncate max-w-[100px]" title={`By: ${user.deletedBy.name}`}>
                            By: {user.deletedBy.name}
                          </p>
                        )}
                      </div>
                    </td>
                  )}

                  {/* Actions */}
                  <td className="py-3 px-3 whitespace-nowrap text-right">
                    {viewMode === "trash" ? (
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleViewClick(user)}
                          className="btn btn-ghost btn-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                        >
                          দেখুন
                        </button>
                        <button
                          onClick={() => handleRestoreUser(user._id)}
                          className="btn btn-ghost btn-xs text-green-600 hover:text-green-800 hover:bg-green-50"
                        >
                          পুনরুদ্ধার
                        </button>
                        <button
                          onClick={() => handlePermanentDelete(user._id)}
                          className="btn btn-ghost btn-xs text-red-600 hover:text-red-800 hover:bg-red-50"
                        >
                          স্থায়ী মুছুন
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        <button
                          onClick={() => handleViewClick(user)}
                          className="btn btn-ghost btn-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                        >
                          দেখুন
                        </button>
                        <button
                          onClick={() => openResetPasswordModal(user)}
                          className="btn btn-ghost btn-xs text-orange-600 hover:text-orange-800 hover:bg-orange-50"
                        >
                          পাসওয়ার্ড
                        </button>

                        {/* NNS ID Management */}
                        <button
                          onClick={() => openNnsIdModal(user)}
                          className="btn btn-ghost btn-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                        >
                          NNS ID
                        </button>

                        <button
                          onClick={() => handleDownloadIdCard(user)}
                          className="btn btn-ghost btn-xs text-teal-600 hover:text-teal-800 hover:bg-teal-50"
                        >
                          ID কার্ড
                        </button>

                        <button
                          onClick={() => openUserFinanceModal(user)}
                          className="btn btn-ghost btn-xs text-purple-600 hover:text-purple-800 hover:bg-purple-50"
                        >
                          আর্থিক
                        </button>

                        {/* Status Toggle - prevent admin from deactivating themselves */}
                        {!(
                          currentUser?.user?._id === user._id &&
                          user.status === "active"
                        ) && (
                          <button
                            onClick={() =>
                              handleStatusChange(
                                user._id,
                                user.status === "active" ? "inactive" : "active"
                              )
                            }
                            className={`btn btn-ghost btn-xs ${
                              user.status === "active"
                                ? "text-yellow-600 hover:text-yellow-800 hover:bg-yellow-50"
                                : "text-green-600 hover:text-green-800 hover:bg-green-50"
                            }`}
                          >
                            {user.status === "active" ? "নিষ্ক্রিয়" : "সক্রিয়"}
                          </button>
                        )}

                        {/* Delete - prevent admin from deleting themselves */}
                        {currentUser?.user?._id !== user._id && (
                          <button
                            onClick={() => handleDeleteUser(user._id)}
                            className="btn btn-ghost btn-xs text-red-600 hover:text-red-800 hover:bg-red-50"
                          >
                            মুছুন
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {paginatedUsers.length === 0 && (
          <div className="text-center py-12">
            <User size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              কোন ব্যবহারকারী পাওয়া যায়নি
            </h3>
            <p className="text-gray-600">
              {viewMode === "trash"
                ? "ট্র্যাশে কোন ব্যবহারকারী নেই।"
                : "বর্তমানে কোন ব্যবহারকারী নেই বা ফিল্টার অনুযায়ী কেউ পাওয়া যায়নি।"}
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {(currentPage - 1) * usersPerPage + 1} to{" "}
            {Math.min(currentPage * usersPerPage, filteredUsers.length)} of{" "}
            {filteredUsers.length} results
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-2 text-sm font-medium rounded-lg ${
                  page === currentPage
                    ? "bg-primary text-white"
                    : "text-gray-600 bg-white border border-gray-300 hover:bg-gray-50"
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* User View Modal */}
      {showViewModal && viewingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-4">
                <div className="relative">
                  {userImageBase64 && !userImageError ? (
                    <img
                      src={userImageBase64}
                      alt={viewingUser.name}
                      className="w-20 h-20 rounded-full object-cover border-4 border-primary"
                      onError={() => setUserImageError(true)}
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full border-4 border-primary bg-primary text-white flex items-center justify-center text-3xl font-bold">
                      {viewingUser.name?.charAt(0)?.toUpperCase() || 'NNS'}
                    </div>
                  )}
                  {viewingUser.status === "active" && (
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 border-2 border-white rounded-full"></div>
                  )}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{viewingUser.name}</h3>
                  <p className="text-sm text-gray-600">
                    {viewingUser.membershipId} | {viewingUser.nnsID || "N/A"}
                  </p>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(
                      viewingUser.role
                    )}`}
                  >
                    {viewingUser.role}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Personal Information */}
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
                    ব্যক্তিগত তথ্য
                  </h4>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">পিতা</p>
                      <p className="text-sm font-medium">{viewingUser.father || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">মাতা</p>
                      <p className="text-sm font-medium">{viewingUser.mother || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">পেশা</p>
                      <p className="text-sm font-medium">{viewingUser.occupation || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">বৈবাহিক অবস্থা</p>
                      <p className="text-sm font-medium">{viewingUser.maritalStatus || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">জন্ম তারিখ</p>
                      <p className="text-sm font-medium">{viewingUser.dob || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">শিক্ষাগত যোগ্যতা</p>
                      <p className="text-sm font-medium">{viewingUser.educationQualification || "N/A"}</p>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
                    যোগাযোগ তথ্য
                  </h4>

                  <div className="space-y-3">
                    <div className="flex items-start">
                      <Phone className="mr-2 text-gray-600 mt-1" size={18} />
                      <div>
                        <p className="text-xs text-gray-500">মোবাইল</p>
                        <p className="text-sm font-medium">{viewingUser.mobile}</p>
                      </div>
                    </div>

                    {viewingUser.email && (
                      <div className="flex items-start">
                        <Mail className="mr-2 text-gray-600 mt-1" size={18} />
                        <div>
                          <p className="text-xs text-gray-500">ইমেইল</p>
                          <p className="text-sm font-medium break-all">{viewingUser.email}</p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-start">
                      <MapPin className="mr-2 text-gray-600 mt-1" size={18} />
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">ঠিকানা</p>
                        <p className="text-sm font-medium">
                          {getLocationName(viewingUser.union, upazila) || "N/A"},
                          {getLocationName(viewingUser.upazila, upazila) || "N/A"},
                          {getLocationName(viewingUser.district, district) || "N/A"},
                          {getLocationName(viewingUser.division, division) || "N/A"}
                        </p>
                      </div>
                    </div>

                    {viewingUser.facebookUrl && (
                      <div>
                        <p className="text-xs text-gray-500">ফেসবুক</p>
                        <a
                          href={viewingUser.facebookUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          প্রোফাইল দেখুন
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Professional Information */}
              <div className="mt-6 space-y-4">
                <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
                  পেশাগত তথ্য
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">প্রতিষ্ঠান</p>
                    <p className="text-sm font-medium">{viewingUser.institute}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">পদবী</p>
                    <p className="text-sm font-medium">{viewingUser.designation}</p>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="mt-6 space-y-4">
                <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
                  অন্যান্য তথ্য
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">এনআইডি</p>
                    <p className="text-sm font-medium">{viewingUser.nid || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">রক্তের গ্রুপ</p>
                    <p className="text-sm font-medium text-red-600">
                      {viewingUser.bloodGroup?.toUpperCase() || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">রক্তদানে সম্মত</p>
                    <p className="text-sm font-medium">
                      {viewingUser.isAgreeDonateBlood ? "হ্যাঁ" : "না"}
                    </p>
                  </div>
                  {viewingUser.lastBloodDonation && (
                    <div>
                      <p className="text-xs text-gray-500">শেষ রক্তদান</p>
                      <p className="text-sm font-medium">{viewingUser.lastBloodDonation}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-gray-500">মাসিক ফি</p>
                    <p className="text-sm font-medium">
                      ৳{viewingUser.monthlyFee || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">স্ট্যাটাস</p>
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadgeColor(
                        viewingUser.status
                      )}`}
                    >
                      {viewingUser.status === "active" && (
                        <CheckCircle size={10} className="mr-1" />
                      )}
                      {viewingUser.status === "inactive" && (
                        <Clock size={10} className="mr-1" />
                      )}
                      {viewingUser.status === "active"
                        ? "সক্রিয়"
                        : viewingUser.status === "inactive"
                        ? "নিষ্ক্রিয়"
                        : "ব্লকড"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer with Download Button */}
            <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-600">
                <p>যোগদানের তারিখ: {new Date(viewingUser.createdAt).toLocaleDateString("bn-BD")}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleDownloadIdCard(viewingUser)}
                  className="btn bg-primary hover:bg-primary-focus text-white flex items-center gap-2"
                >
                  <Download size={18} />
                  ID কার্ড ডাউনলোড
                </button>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="btn btn-ghost"
                >
                  বন্ধ করুন
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NNS ID Modal */}
      {showNnsIdModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                NNS ID পরিচালনা
              </h3>
              <button
                onClick={() => setShowNnsIdModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle size={24} />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  ব্যবহারকারী:{" "}
                  <span className="font-semibold">{selectedUser?.name}</span>
                </p>
                <p className="text-sm text-gray-600">
                  বর্তমান NNS ID:{" "}
                  <span className="font-mono">
                    {selectedUser?.nnsID || "Not Assigned"}
                  </span>
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="nnsId"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    নতুন NNS ID
                  </label>
                  <input
                    type="text"
                    id="nnsId"
                    value={nnsId}
                    onChange={(e) => setNnsId(e.target.value)}
                    placeholder="NNS-XXXX-XXXX"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertTriangle
                      size={20}
                      className="text-yellow-600 mr-3 mt-0.5 flex-shrink-0"
                    />
                    <div className="text-sm text-yellow-800">
                      <p className="font-semibold mb-1">গুরুত্বপূর্ণ:</p>
                      <p>
                        NNS ID ছাড়া ব্যবহারকারী সক্রিয় করা যাবে না। নিশ্চিত
                        করুন যে ID টি সঠিক এবং অনন্য।
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowNnsIdModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                বাতিল
              </button>
              <button
                onClick={handleNnsIdUpdate}
                disabled={updateNnsIdMutation.isLoading}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
              >
                {updateNnsIdMutation.isLoading
                  ? "আপডেট হচ্ছে..."
                  : "আপডেট করুন"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Reset Password
              </h3>
              <button
                onClick={() => setShowResetPasswordModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle size={24} />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  User:{" "}
                  <span className="font-semibold">{selectedUser?.name}</span>
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="newPassword"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    New Password
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowResetPasswordModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleResetPassword}
                disabled={resetPasswordMutation.isLoading}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
              >
                {resetPasswordMutation.isLoading
                  ? "Saving..."
                  : "Save Password"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Finance Modal */}
      {showUserFinanceModal && selectedUserForFinance && (
        <UserFinanceModal
          user={selectedUserForFinance}
          isOpen={showUserFinanceModal}
          onClose={() => {
            setShowUserFinanceModal(false);
            setSelectedUserForFinance(null);
          }}
        />
      )}
    </div>
  );
}

// User Finance Modal Component
const UserFinanceModal = ({ user, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState("summary");
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch user financial summary
  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ["userFinanceSummary", user._id],
    queryFn: async () => await api.get(`finance/user/${user._id}/summary`),
    enabled: isOpen && activeTab === "summary",
    staleTime: 2 * 60 * 1000,
  });

  // Fetch user transactions
  const { data: transactionsData, isLoading: transactionsLoading } = useQuery({
    queryKey: ["userFinanceTransactions", user._id, currentPage],
    queryFn: async () =>
      await api.get(
        `finance/user/${user._id}/transactions?page=${currentPage}&limit=10`
      ),
    enabled: isOpen && activeTab === "transactions",
    staleTime: 2 * 60 * 1000,
  });

  const summary = summaryData?.data?.data || {};
  const transactions = transactionsData?.data?.data?.transactions || [];
  const pagination = transactionsData?.data?.data?.pagination || {};

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("bn-BD", {
      style: "currency",
      currency: "BDT",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "bg-yellow-100 text-yellow-800", text: "অপেক্ষমাণ" },
      approved: { color: "bg-green-100 text-green-800", text: "অনুমোদিত" },
      rejected: { color: "bg-red-100 text-red-800", text: "প্রত্যাখ্যাত" },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}
      >
        {config.text}
      </span>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <img
              src={
                user.imageUrl
                  ? `${baseUrl}${user.imageUrl}`
                  : "/default-avatar.png"
              }
              alt={user.name}
              className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
            />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {user.name} এর আর্থিক তথ্য
              </h3>
              <p className="text-sm text-gray-600">
                সদস্য নং: {user.membershipId} | NNS ID: {user.nnsID || "N/A"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XCircle size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab("summary")}
            className={`px-6 py-3 text-sm font-medium border-b-2 ${
              activeTab === "summary"
                ? "border-primary text-primary"
                : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            সারসংক্ষেপ
          </button>
          <button
            onClick={() => setActiveTab("transactions")}
            className={`px-6 py-3 text-sm font-medium border-b-2 ${
              activeTab === "transactions"
                ? "border-primary text-primary"
                : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            লেনদেনের ইতিহাস
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {activeTab === "summary" && (
            <div className="space-y-6">
              {summaryLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : (
                <>
                  {/* Statistics Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <div className="flex items-center">
                        <CheckCircle className="w-8 h-8 text-green-600 mr-3" />
                        <div>
                          <p className="text-sm text-green-600 font-medium">
                            মোট প্রদত্ত
                          </p>
                          <p className="text-2xl font-bold text-green-800">
                            {formatAmount(summary.totalPaid)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                      <div className="flex items-center">
                        <Clock className="w-8 h-8 text-yellow-600 mr-3" />
                        <div>
                          <p className="text-sm text-yellow-600 font-medium">
                            অপেক্ষমাণ
                          </p>
                          <p className="text-2xl font-bold text-yellow-800">
                            {formatAmount(summary.totalPending)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                      <div className="flex items-center">
                        <XCircle className="w-8 h-8 text-red-600 mr-3" />
                        <div>
                          <p className="text-sm text-red-600 font-medium">
                            প্রত্যাখ্যাত
                          </p>
                          <p className="text-2xl font-bold text-red-800">
                            {formatAmount(summary.totalRejected)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payment Status */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                      পেমেন্ট স্ট্যাটাস
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">মাসিক ফি:</p>
                        <p className="font-semibold">
                          {formatAmount(user.monthlyFee)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">সর্বশেষ পেমেন্ট:</p>
                        <p className="font-semibold">
                          {user.lastPaymentDate
                            ? new Date(user.lastPaymentDate).toLocaleDateString(
                                "bn-BD"
                              )
                            : "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">মোট পেমেন্ট:</p>
                        <p className="font-semibold">
                          {summary.totalTransactions || 0} টি
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">অনুমোদিত পেমেন্ট:</p>
                        <p className="font-semibold text-green-600">
                          {summary.approvedTransactions || 0} টি
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === "transactions" && (
            <div className="space-y-4">
              {transactionsLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : transactions.length > 0 ? (
                <>
                  <div className="space-y-3">
                    {transactions.map((transaction) => (
                      <div
                        key={transaction._id}
                        className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">
                              {transaction.title}
                            </p>
                            <p className="text-sm text-gray-600">
                              {transaction.description}
                            </p>
                            <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                              <span>
                                {new Date(
                                  transaction.transactionDate
                                ).toLocaleDateString("bn-BD")}
                              </span>
                              {transaction.paymentMonth && (
                                <span>মাস: {transaction.paymentMonth}</span>
                              )}
                              {transaction.paymentMethod && (
                                <span>
                                  পদ্ধতি: {transaction.paymentMethod}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-green-600">
                              {formatAmount(transaction.amount)}
                            </p>
                            {getStatusBadge(transaction.status)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {pagination.pages > 1 && (
                    <div className="flex items-center justify-between pt-4">
                      <p className="text-sm text-gray-600">
                        দেখানো হচ্ছে {(pagination.current - 1) * 10 + 1} থেকে{" "}
                        {Math.min(pagination.current * 10, pagination.total)}{" "}
                        পর্যন্ত, মোট {pagination.total}
                      </p>
                      <div className="flex space-x-2">
                        <button
                          onClick={() =>
                            setCurrentPage((prev) => Math.max(prev - 1, 1))
                          }
                          disabled={!pagination.hasPrev}
                          className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                        >
                          পূর্ববর্তী
                        </button>
                        <button
                          onClick={() =>
                            setCurrentPage((prev) =>
                              Math.min(prev + 1, pagination.pages)
                            )
                          }
                          disabled={!pagination.hasNext}
                          className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                        >
                          পরবর্তী
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <CreditCard size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">কোনো লেনদেন পাওয়া যায়নি</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
          >
            বন্ধ করুন
          </button>
        </div>
      </div>
    </div>
  );
};

export default AllUser;
